/**
 * Pure builders for the graph queries of the Projects screen.
 *
 * Kept free of app imports so tests can compile the exact production query
 * strings against the real query engine without mounting the screen.
 */

import { TASK_STATUS_OPTIONS } from "@/components/graph/provisioned";

/** Live list of every project row in a Projects database, sorted by Title. */
export const buildProjectsListQuery = (projectsDatabaseId: string): string =>
  `databases.["${projectsDatabaseId}"].rows sort by Title`;

/**
 * Graph source of tasks for count filters.
 *
 * - Builtin personal tasks must be queried as `tasks`: that type exposes the
 *   virtual Status column (Completed/Discarded/Todo). Querying the extension
 *   table via `databases.["table:task_extensions…"].rows` only sees extension
 *   properties (Project, …) and fails with `name 'Status' not found`.
 * - Org provisioned Task types use `databases.["${id}"].rows` and carry a
 *   real Status select.
 */
export type ProjectTasksSource =
  | { kind: "builtin" }
  | { kind: "database"; id: string };

export const projectTasksSource = (source: ProjectTasksSource): string =>
  source.kind === "builtin" ? "tasks" : `databases.["${source.id}"].rows`;

/**
 * Tasks whose Project relation points at `projectId` (row id). Status
 * filters are optional.
 */
export const buildProjectTasksFilterQuery = (
  source: ProjectTasksSource,
  projectId: string,
  statuses?: readonly string[]
): string => {
  const projectFilter = `Project._routine.id = "${projectId}"`;
  const statusFilter =
    statuses && statuses.length > 0
      ? ` and (${statuses
          .map((status) => `Status = "${status}"`)
          .join(" or ")})`
      : "";
  return `${projectTasksSource(source)} where ${projectFilter}${statusFilter}`;
};

export const buildProjectTasksCountQuery = (
  source: ProjectTasksSource,
  projectId: string,
  statuses?: readonly string[]
): string =>
  `length(${buildProjectTasksFilterQuery(source, projectId, statuses)})`;

/**
 * One query returning, for each project id (positional),
 * `[closedCount, totalCount]` where closed = Completed + Discarded.
 * Batching avoids a compile+run RPC pair per project.
 */
export const buildProjectTaskCountsQuery = (
  source: ProjectTasksSource,
  projectIds: string[]
): string => {
  const closed = [
    TASK_STATUS_OPTIONS.completed,
    TASK_STATUS_OPTIONS.discarded,
  ] as const;
  return `[${projectIds
    .map(
      (projectId) =>
        `[${buildProjectTasksCountQuery(source, projectId, closed)}, ${buildProjectTasksCountQuery(source, projectId)}]`
    )
    .join(", ")}]`;
};
